import { endOfDay, format, subDays } from "date-fns";
import type { OpportunityBoardFilters } from "@forgepoint/shared";
import {
  CREATED_RANGE_PRESETS,
  type CreatedRangePreset,
  type OpportunityCreatedRange,
} from "./opportunityFilterTypes";

/**
 * Resolves a created-date selection into the filter bounds sent to the server.
 * A preset's window is measured back from the given instant, so callers resolve
 * per request rather than storing the resulting dates.
 * @param range - The current selection, or null when the filter is off
 * @param now - The instant a preset window is measured back from
 * @returns The createdAfter and createdBefore bounds to apply
 */
export function resolveCreatedRange(
  range: OpportunityCreatedRange | null,
  now: Date,
): Pick<OpportunityBoardFilters, "createdAfter" | "createdBefore"> {
  if (!range) {
    return {};
  }
  if (range.kind === "preset") {
    return { createdAfter: subDays(now, presetFor(range.preset).days) };
  }
  // The end is inclusive of its whole day, not midnight at its start.
  return {
    createdAfter: range.from,
    createdBefore: range.to && endOfDay(range.to),
  };
}

/**
 * Summarizes a created-date selection for its filter chip.
 * @param range - The current selection, or null when the filter is off
 * @returns The chip summary, or null when nothing is selected
 */
export function createdRangeSummary(
  range: OpportunityCreatedRange | null,
): string | null {
  if (!range) {
    return null;
  }
  if (range.kind === "preset") {
    return presetFor(range.preset).label;
  }
  const from = format(range.from, "MMM d, yyyy");
  return range.to
    ? `${from} – ${format(range.to, "MMM d, yyyy")}`
    : `${from} –`;
}

/**
 * Looks up a preset's window length and label.
 * @param preset - The preset to look up
 * @returns The matching preset definition
 */
function presetFor(preset: CreatedRangePreset) {
  return (
    CREATED_RANGE_PRESETS.find((candidate) => candidate.value === preset) ??
    CREATED_RANGE_PRESETS[0]
  );
}
